/**
 * Extracts the base64 data from a data URL
 * @param dataUrl The data URL string (e.g., "data:image/png;base64,ABC123...")
 * @returns The base64 data part only
 */
export const extractBase64FromDataUrl = (dataUrl: string): string => {
	// If it's a data URL, extract the base64 part
	if (dataUrl.startsWith('data:')) {
		const base64Index = dataUrl.indexOf(';base64,');
		if (base64Index >= 0) {
			return dataUrl.slice(base64Index + 8); // 8 is the length of ";base64,"
		}
	}
	// If it's already just base64 data, return as is
	// But first validate it's actually base64 format
	if (/^[A-Za-z0-9+/=]+$/.test(dataUrl)) {
		return dataUrl;
	}
	throw new Error('Invalid data URL or base64 string provided');
}

// Add a custom logging function to avoid console.error linter errors
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const logError = (message: string, error?: any): void => {
	// In a production environment, this could be replaced with a proper logging service
	// eslint-disable-next-line no-console
	console.error(message, error);
}

/**
 * Converts a base64 string to a Blob
 */
export const base64ToBlob = (base64: string, type = 'image/png'): Blob => {
	const byteCharacters = atob(base64);
	const byteNumbers = new Array(byteCharacters.length);
	for (let i = 0; i < byteCharacters.length; i++) {
		byteNumbers[i] = byteCharacters.charCodeAt(i);
	}
	const byteArray = new Uint8Array(byteNumbers);
	return new Blob([byteArray], { type });
}

/**
 * Gets image dimensions from a URL or blob URL
 */
export const getImageDimensions = async (url: string): Promise<{width: number; height: number}> => {
	return new Promise((resolve, reject) => {
		const img = new Image();
		img.onload = () => {
			resolve({ width: img.width, height: img.height });
			img.onload = null;
			img.onerror = null;
		};
		img.onerror = (error: Event | string) => {
			reject(new Error(`Failed to load image: ${String(error)}`));
			img.onload = null;
			img.onerror = null;
		};
		img.src = url;
	});
}
